import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Breadcrumbs, Stack, Button } from "@mui/material";
import Link from "@mui/material/Link";
import InputBase from "@mui/material/InputBase";
import Divider from "@mui/material/Divider";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { BiEuro } from "react-icons/bi";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate,NavLink} from "react-router-dom";
import { alertActions } from "../_actions";
import ToastMessageComp from "../component/ToastMessageComp";
import TextField from "@mui/material/TextField";
import DialogContentText from "@mui/material/DialogContentText";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import LoadingButton from "@mui/lab/LoadingButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { frFR } from "@mui/x-date-pickers/locales";
import dayjs from "dayjs";
import "dayjs/locale/fr.js";
import Anchor from "../_helpers/anchor";
import PageLoaderComp from "../component/pageLoaderComp";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    // backgroundColor: theme.palette.common.black,
    // color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.common.white,
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '20ch',
      '&:focus': {
        width: '30ch',
      },
    },
  },
}));

export default function InvoiceMonthly() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    status: '',
  });
  // Sorting states removed
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const alert = useSelector((state) => state.alert);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        ...(filters.startDate && { startDate: moment(filters.startDate).format('YYYY-MM-DD') }),
        ...(filters.endDate && { endDate: moment(filters.endDate).format('YYYY-MM-DD') }),
        ...(filters.status && { status: filters.status }),
      };

      const response = await axios.get(`${process.env.REACT_APP_API_URL}api/admin/monthly-summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      // Ensure we have an array and parse the invoicedata field
      const processedData = Array.isArray(response.data.data) 
        ? response.data.data.map(item => ({
            ...item,
            // Parse the invoicedata JSON string if it exists
            invoicedata: item.invoicedata ? (typeof item.invoicedata === 'string' 
              ? JSON.parse(item.invoicedata) 
              : item.invoicedata) 
              : null
          }))
        : [];

      setData(processedData);
      setTotalCount(response.data.total || processedData.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      dispatch(alertActions.error(error.response?.data?.message || 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, filters, dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(timer);
  }, [fetchData]);

  // Sorting handler removed

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0);
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      status: '',
    });
    setSearchTerm('');
    setPage(0);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExport = (format) => {
    // Implement export functionality
    console.log(`Exporting as ${format}`);
    handleMenuClose();
  };

  const columns = [
    { id: 'invoice_month', label: 'Month', minWidth: 120 },
    { 
      id: 'invoicedata', 
      label: 'Total Amount', 
      minWidth: 120, 
      format: (value, row) => {
        try {
          // Check if value is already parsed (from fetchData) or needs parsing
          const data = typeof value === 'string' ? JSON.parse(value) : value;
          return `€${data?.total?.toFixed(2) || '0.00'}`;
        } catch (e) {
          return 'N/A';
        }
      }
    },
    { 
      id: 'invoice_status', 
      label: 'Status', 
      minWidth: 100, 
      format: (value) => {
        switch(value) {
          case 0: return 'Pending';
          case 1: return 'Paid';
          default: return 'Pending';
        }
      } 
    },
    { 
      id: 'customer', 
      label: 'Customer', 
      minWidth: 200, 
      format: (_, row) => `${row.firstname || ''} ${row.lastname || ''}`.trim() || 'N/A'
    },
    { id: 'email', label: 'Email', minWidth: 200 },
    { 
      id: 'actions', 
      label: 'Actions', 
      minWidth: 100, 
      align: 'center',
      format: (_, row) => (
        <Tooltip title="View Invoice">
          <IconButton
            size="small"
            onClick={() => window.open(`/invoices/${row.invoicepath}`, '_blank')}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    },
  ];

  const statusOptions = [
    { value: '', label: 'All' },
    { value: '0', label: 'Pending' },
    { value: '1', label: 'Paid' },
    { value: '2', label: 'Overdue' },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <ToastMessageComp />
      <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
       
         <Typography style={{float:"left",width:"60%"}} variant="h4" component="div" fontWeight={500} color={"#1CBE93"}>Facture mensuelle</Typography>
        <Search>
          <SearchIcon sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <StyledInputBase
            placeholder="Search…"
            inputProps={{ 'aria-label': 'search' }}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </Search>
        
        <Tooltip title="Filter list">
          <IconButton onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <Typography variant="subtitle1" gutterBottom>Filters</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
            <Stack spacing={2}>
              <DatePicker
                label="Start Date"
                value={filters.startDate ? dayjs(filters.startDate) : null}
                onChange={(newValue) => handleFilterChange('startDate', newValue ? newValue.toDate() : null)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                inputFormat="DD/MM/YYYY"
              />
              <DatePicker
                label="End Date"
                value={filters.endDate ? dayjs(filters.endDate) : null}
                onChange={(newValue) => handleFilterChange('endDate', newValue ? newValue.toDate() : null)}
                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                inputFormat="DD/MM/YYYY"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                size="small"
                fullWidth
              >
                Reset Filters
              </Button>
            </Stack>
          </LocalizationProvider>
        </Box>
      </Menu>



    <Paper sx={{ width: '100%', overflow: 'hidden', mb: 2 }}>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <StyledTableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <PageLoaderComp />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <StyledTableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                  {columns.map((column) => {
                    const value = row[column.id];
                    
                    if (column.id === 'actions') {
                      return (
                        <StyledTableCell key={column.id} align="center">
                          <Tooltip title="View Invoice">
                            <IconButton size="small">
                              <Link target="_blank" href={`${process.env.REACT_APP_AWS_URL_STATIC_BUSYPLACE}assets/uploads/pdfgenerate/invoice/${row.invoicepath}`}>
                                <VisibilityIcon fontSize="small" />
                              </Link>
                            </IconButton>
                          </Tooltip>
                        </StyledTableCell>
                      );
                    }
                    
                    return (
                      <StyledTableCell key={column.id} align={column.align || 'left'}>
                        {column.format ? column.format(value, row) : value}
                      </StyledTableCell>
                    );
                  })}
                </StyledTableRow>
              ))
            )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
